/**
 * O PERÍODO NA URL — a busca das telas que filtram por `de` e `ate`.
 *
 * ## Por que ele sai do `useState`
 *
 * O manual (p.16) é direto: *"o estado dos filtros vai para a URL, para o
 * usuário salvar e compartilhar a visão"*. Com o período em `useState`,
 * recarregar a página volta sempre aos últimos 30 dias, e mandar o link manda
 * uma tela diferente da que se estava vendo — o que é pior do que não mandar,
 * porque quem recebe acha que está vendo a mesma coisa.
 *
 * Num painel de gestão isso tem consequência direta: a conversa é "olha esse
 * mês aqui", e o link não leva o mês.
 *
 * ## Duas telas, um validador
 *
 * A Visão do gestor e Custos filtram o mesmo período, com o mesmo padrão de 30
 * dias. Dois validadores iguais divergiriam na primeira correção — e o que
 * ficasse para trás aceitaria a data que o outro recusa.
 *
 * ## O PADRÃO NÃO VAI PARA A URL
 *
 * `montarBuscaDePeriodo` devolve só o que difere do padrão, como os outros
 * validadores do projeto fazem. Pôr tudo deixaria a barra de endereço cheia de
 * parâmetro que não diz nada, e o link de "a visão de sempre" ficaria
 * indistinguível do link de uma visão escolhida.
 */

import { janelaAteHoje } from './dia-da-operacao';

/** O formato que o servidor aceita, e o único que entra na URL. */
const DATA = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

export interface BuscaDePeriodo {
  readonly de?: string;
  readonly ate?: string;
}

/** Quantos dias o padrão cobre. O mesmo nas duas telas. */
export const DIAS_DO_PERIODO_PADRAO = 30;

/**
 * O período padrão: os últimos trinta dias, terminando hoje.
 *
 * Inteiro NO FUSO DA OPERAÇÃO (20/10/2026): antes era inteiro em UTC, e depois
 * das 21:00 em São Paulo o "hoje" já era amanhã. Os dois lados saem da mesma
 * conta (`janelaAteHoje`) — misturar um `de` de um fuso com um `ate` de outro
 * produz período de 31 dias, o tipo de erro que ninguém vê.
 */
export function periodoPadrao(agora: Date = new Date()): { readonly de: string; readonly ate: string } {
  return janelaAteHoje(DIAS_DO_PERIODO_PADRAO, agora);
}

/**
 * Lê a busca da rota. Valor torto cai no padrão, em silêncio.
 *
 * SILÊNCIO É A ESCOLHA CERTA AQUI: um link colado errado, ou cortado pelo
 * aplicativo de mensagem, não é erro de quem clicou — mostrar "data inválida"
 * para quem abriu um link transfere a culpa para a pessoa errada. A tela abre
 * no padrão, que é o que ela abriria sem link nenhum.
 *
 * PERÍODO INVERTIDO TAMBÉM CAI NO PADRÃO. `de` depois de `ate` não é um
 * período pequeno: é um período impossível, e o servidor o recusaria. Corrigir
 * trocando os dois de lugar seria adivinhar o que a pessoa quis.
 */
export function validarBuscaDePeriodo(entrada: Record<string, unknown>): BuscaDePeriodo {
  const de = typeof entrada['de'] === 'string' && DATA.test(entrada['de']) ? entrada['de'] : undefined;
  const ate = typeof entrada['ate'] === 'string' && DATA.test(entrada['ate']) ? entrada['ate'] : undefined;

  /* Os dois ou nenhum: um `de` sozinho com o `ate` do padrao produz um periodo
     que a pessoa nao escolheu, e que muda de tamanho conforme o dia em que o
     link e aberto. */
  if (de === undefined || ate === undefined) return {};
  if (de > ate) return {};
  return { de, ate };
}

/** Monta a busca para navegar, deixando de fora o que já é o padrão. */
export function montarBuscaDePeriodo(
  periodo: { readonly de: string; readonly ate: string },
  agora: Date = new Date(),
): BuscaDePeriodo {
  const padrao = periodoPadrao(agora);
  if (periodo.de === padrao.de && periodo.ate === padrao.ate) return {};
  return { de: periodo.de, ate: periodo.ate };
}

/**
 * O período que a tela usa: o da URL quando ele existe, o padrão quando não.
 *
 * Devolve também se ele veio da URL, porque a tela escreve isso: um período
 * escolhido e o período de sempre se parecem na faixa, e saber qual é muda o
 * que a pessoa conclui do número.
 */
export function periodoDaBusca(
  busca: BuscaDePeriodo,
  agora: Date = new Date(),
): { readonly de: string; readonly ate: string; readonly daUrl: boolean } {
  const padrao = periodoPadrao(agora);
  if (busca.de === undefined || busca.ate === undefined) return { ...padrao, daUrl: false };
  return { de: busca.de, ate: busca.ate, daUrl: true };
}
